/**
 * Общий refresh/redirect state для auth.interceptor.ts и error.interceptor.ts.
 *
 * @fix fix-auth-401-zombie r3 (P1/P2, gpt): вынесено в отдельный модуль,
 * потому что оба интерсептора ДОЛЖНЫ шарить это состояние, а не иметь
 * каждый свою копию:
 *
 * - P1: error.interceptor раньше звал authService.getFreshToken(true)
 *   НАПРЯМУЮ, мимо single-flight дедупликации auth.interceptor — два 401,
 *   разнесённых по времени (или параллельных), давали ДВА отдельных
 *   форс-рефреша (updateToken(-1)) вместо одного общего in-flight промиса.
 *   Форс- и обычный (проактивный) рефреш держатся в ОТДЕЛЬНЫХ промисах
 *   (getFreshToken(true) и getFreshToken(false) — семантически разные
 *   вызовы updateToken с разной minValidity, дедуплицировать их вместе
 *   было бы некорректно), но каждый вид дедуплицируется сам по себе
 *   независимо от того, ИЗ КАКОГО интерсептора пришёл вызов.
 *
 * - P2 (обе lane): triggerLoginRedirect() раньше делал `void login()` без
 *   .catch() — если login() сам отклонялся (заблокированный
 *   localStorage/iframe-policy браузера), redirectInProgress залипал
 *   НАВСЕГДА, и все последующие protected-запросы тихо превращались в
 *   EMPTY без тоста и без реального редиректа — новый вариант той же
 *   "зомби-сессии", только тише. Теперь: .catch() сбрасывает флаг (можно
 *   попробовать снова на следующий protected-запрос) + console.error +
 *   опциональный toast-fallback callback (используется error.interceptor,
 *   у которого есть доступ к MessageService/TranslateService).
 */
import { AuthService } from '../services/auth.service';

let inFlightNormalRefresh: Promise<string | null> | null = null;
let inFlightForceRefresh: Promise<string | null> | null = null;
let redirectInProgress = false;

export function getSharedFreshToken(authService: AuthService, force = false): Promise<string | null> {
  if (force) {
    if (!inFlightForceRefresh) {
      inFlightForceRefresh = authService.getFreshToken(true).finally(() => {
        inFlightForceRefresh = null;
      });
    }
    return inFlightForceRefresh;
  }
  if (!inFlightNormalRefresh) {
    inFlightNormalRefresh = authService.getFreshToken(false).finally(() => {
      inFlightNormalRefresh = null;
    });
  }
  return inFlightNormalRefresh;
}

export function triggerLoginRedirect(authService: AuthService, onFailure?: () => void): void {
  if (redirectInProgress) return;
  redirectInProgress = true;
  authService.login().catch((err: unknown) => {
    console.error('[Auth] login() redirect failed', err instanceof Error ? err.message : err);
    redirectInProgress = false;
    onFailure?.();
  });
}

/** Test-only: сброс module-level singleton state между тестами (в реальном
 * браузере login() уводит со страницы и обнуляет это естественным
 * образом; jsdom — нет). */
export function _resetAuthRefreshStateForTests(): void {
  inFlightNormalRefresh = null;
  inFlightForceRefresh = null;
  redirectInProgress = false;
}
